<template>
  <div v-if="!isConfirmed">
    <div class="form-group">
      <label class="form-label">Date</label>
      <ValidationProvider rules="required" ref="date" v-slot="{ errors }">
        <InquiryDateInput v-model="date" />
        <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
      </ValidationProvider>
    </div>

    <div class="form-group">
      <label class="form-label">Lesson</label>
      <ValidationProvider rules="required" ref="lesson" v-slot="{ errors }">
        <SelectLessonInput
          v-if="date && targetTutor" :key="date+targetTutor.id"
          :date="date" :tutor="targetTutor" v-model="lesson" />
          <div v-else class="text-danger">Please select Tutor and Date</div>
          <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
        </ValidationProvider>
        <div v-if="lesson?.isApproved == false" class="text-danger">
          Application cannot be registered because the previous inquiry has not been approved.
        </div>
    </div>

    <div class="form-group">
      <label>Type</label>
      <ValidationProvider rules="required" ref="inquiryType" v-slot="{ errors }">
        <div v-if="targetTutor?.id == lesson?.substituteTutor?.tutorId">
          {{ inquiryTypes['classRecord'] }}
        </div>
        <select v-else v-model="inquiryType" class="form-control">
          <option :value="null" disabled>Please select one</option>
          <option v-for="(name, type) of inquiryTypes" :key="type" :value="type">{{ name }}</option>
        </select>
        <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
      </ValidationProvider>
    </div>

    <div class="form-group">
      <label class="form-label">Student</label>
      <div v-if="lesson?.isApproved == false" class="text-danger">
        Application cannot be registered.
      </div>
      <ValidationProvider v-else rules="required" ref="student" v-slot="{ errors }">
        <select v-if="lesson" v-model="student" class="form-control">
          <option :value="null" disabled>Please select one</option>
          <option v-for="{ student } of lesson.students" :key="student.id" :value="student">
            {{ student.name }}
          </option>
        </select>
        <div v-else class="text-danger">Please select Lesson</div>
        <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
      </ValidationProvider>
    </div>

    <div class="form-group">
      <label class="form-label">Comment</label>
      <ValidationProvider ref="comment" v-slot="{ errors }">
        <Textarea class="form-control" v-model="comment" />
        <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
      </ValidationProvider>
    </div>
  </div>

  <div v-else>
    <div class="form-group">
      <label>Date</label>
      <div class="form-confirm-value">{{ lesson.date | ymd }}</div>
    </div>

    <div class="form-group">
      <label>Lesson</label>
      <div class="form-confirm-value">
        Period {{ lesson.period }} {{ lesson.campus.name }}
        {{ lesson.course.name }} {{ lesson.level.name }}
      </div>
    </div>

    <div class="form-group">
      <label>Type</label>
      <div class="form-confirm-value">{{ inquiryType | inquiryType }}</div>
    </div>

    <div class="form-group">
      <label>Student</label>
      <div class="form-confirm-value">{{ student.name }}</div>
    </div>

    <div class="form-group">
      <label>Comment</label>
      <div class="form-confirm-value">{{ comment }}</div>
    </div>

    <div class="form-group" v-if="isExecuted()">
      <label>{{ status | status }} Comment</label>
      <div class="form-confirm-value">{{ approvedComment }}</div>
    </div>
  </div>

</template>

<script lang="ts">
import _ from 'lodash'
import moment, { Moment } from 'moment'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { ValidationProvider } from 'vee-validate'
import { Tutor } from '@/store/tutors/types'
import { IAddApplicationComponent } from '@/components/pages/application/Add.vue'
import InquiryDateInput from '@/components/parts/inputItem/InquiryDateInput.vue'
import SelectLessonInput from '@/components/parts/inputItem/SelectLessonInput.vue'
import Textarea from '@/components/parts/inputItem/Textarea.vue'
import { EvaluationInquiryApplication, inquiryType, InquiryTypes } from '@/store/applications/evaluationInquiries/types'
import { Lesson, Student } from '@/store/lessons/types'
import { applicationStatusCode, ApplicationStatus } from '@/store/applications/types'

@Component({
  components: {
    ValidationProvider,
    InquiryDateInput,
    SelectLessonInput,
    Textarea,
  },
  filters: {
    ymd(date: Date) {
      return moment(date).format('YYYY/MM/DD')
    },
    inquiryType: (type: inquiryType) => InquiryTypes[type],
    status: (s: applicationStatusCode): string => ApplicationStatus[s],
  },
})
export default class ApplicationAddEvaluationInquiry
  extends Vue
  implements IAddApplicationComponent {
  @Prop({ required: true }) isConfirmed!: boolean

  @Prop() targetTutor?: Tutor

  // 編集時・詳細時など、初期値が必要な場合
  @Prop() initialValues!: EvaluationInquiryApplication

  @Prop() status!: applicationStatusCode

  private date: Moment|null = null

  private lesson: Lesson|null = null

  private inquiryType: inquiryType = 'classRecord'

  private student: Student|null = null

  private comment: string|null = null

  private approvedComment: string|null = null

  private isValidated: boolean = false

  private inquiryTypes = InquiryTypes

  private created() {
    if (this.initialValues) {
      this.date = this.initialValues.lesson.date
      this.lesson = this.initialValues.lesson
      this.inquiryType = this.initialValues.inquiryType
      this.student = this.initialValues.student
      this.comment = this.initialValues.comment
      this.approvedComment = this.initialValues?.approvedComment || null
    }
  }

  /**
   * バリデーション（ApplicationAdd.vueから呼び出し）
   * @return {Promise} バリデーション結果
   */
  public validation(): Promise<boolean> {
    return Promise.all([
      'date', 'lesson', 'inquiryType', 'student', 'comment',
    ].map((key) => (this.$refs[key] as any).validate()))
      .then((validationResults) => {
        const errorLength = _.filter(validationResults, { valid: false }).length

        if (errorLength === 0 && this.lesson!.isApproved) {
          this.isValidated = true
          return true
        }
        return false
      })
  }

  /**
   * 登録（ApplicationAdd.vueから呼び出し）
   * @return {Promise} ActionからのPromise
   */
  public register(tutor: Tutor): Promise<void> {
    const application: EvaluationInquiryApplication = {
      type: 'inquiry',
      inquiryType: this.inquiryType as inquiryType,
      student: this.student as Student,
      lesson: this.lesson as Lesson,
      comment: this.comment,
    }

    return this.$store.dispatch('registerEvaluationInquiryApplication', application)
  }

  /**
   * 申請のステータスが未承認以外かチェック
   */
  private isExecuted() {
    return this.status !== 'unapproved'
  }
}
</script>

<style scoped>
</style>
